/**
 * 上传前把图片压到长边 maxEdge 的 JPEG。移植自稿子的 `ATT.shrink()`。
 *
 * 两个调用方，都是「看清内容就够」而不是「留念」：
 *   * **天级作业图**（拍老师写的黑板，uploadShots）—— 480px
 *   * **成员头像**（/people 的头像 sheet）—— 256px，最大只渲染到 64px
 *
 * 为什么在客户端压：手机原图 3–8MB，家里上行慢；而这些图的用途是「看清老师写了
 * 什么」/「认出是谁」，几百 px 够。服务器那边是 Kamal 的单个持久卷，不是对象存储。
 *
 * ⚠️ 条目级证据（「拍给爸妈看」，uploadProofs）**刻意不走这里** —— 那张照片是留念，
 * 爸妈会放大看，压过就没意义了。别顺手把它接回来，见 uploadProofs 上的注释。
 *
 * 压不了就**原样返回** —— HEIC 在部分浏览器上解不出来、canvas 在隐私模式下会被挡。
 * 服务端的 2MB 上限会兜住，用户看到的是「这张没上去」而不是静默失败。
 * 服务端那道校验是安全边界，这里只是便利。
 */
export async function shrinkImage(file: File, maxEdge = 480): Promise<File> {
  const img = await load(file)
  if (!img) return file

  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.max(1, Math.round(img.width * scale))
  const h = Math.max(1, Math.round(img.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(img, 0, 0, w, h)

  const blob = await new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/jpeg', 0.72)
    } catch {
      resolve(null)
    }
  })
  if (!blob) return file

  const stem = file.name.replace(/\.[^.]+$/, '') || 'photo'
  return new File([blob], `${stem}.jpg`, { type: 'image/jpeg' })
}

function load(file: File): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}
